import { Injectable } from '@angular/core';

import { ApiModel } from 'models';
import { OrganizationType } from 'shared/services';
import { Observable, of } from 'rxjs';
import { BranchToCreate } from 'models/organization.model';
import { catchError } from 'rxjs/operators';
import { Auth0Organization } from 'shared/services/authenticatedUser/types';
import { Organization } from 'shared/services/authenticatedUser/types';

@Injectable()
export class AuthenticationModel {
    constructor(private apiModel: ApiModel) {}

    public requestForTeamInvite(data: { organizationId; firstName; lastName; email; preferredLanguage }) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getInvitationsEndpoint()}/request`,
            body: data
        });
    }

    public searchOrganization(organization: {
        type?: 'retailer' | 'manufacturer';
        name: string;
    }): Observable<SearchOrganizationResponse[]> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getOrganizationsEndpoint()}/search`,
            params: {
                name: organization.name,
                ...(organization?.type ? { type: organization.type } : {})
            }
        });
    }

    public signup(requestId: string) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getSignupEndpoint()}/new`,
            body: { requestId }
        });
    }

    public submitSignupRequest(signupRequest: SignupRequest) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getSignupEndpoint()}/contact`,
            body: signupRequest
        });
    }

    public getSignupRequest(signupRequestId: string) {
        return this.apiModel.makeHttpObservableRequest<SignupRequest>({
            method: 'GET',
            url: `${this.apiModel.getSignupEndpoint()}/request/${signupRequestId}`
        });
    }

    public resetPassword(data: { email: string }) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getUsersEndpoint()}/reset-password`,
            body: { email: data.email }
        });
    }

    public resetPasswordCheckCode(code: string) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getUsersEndpoint()}/reset-password-check-code`,
            body: {
                code
            }
        });
    }

    public resetPasswordWithCode(code: string, password: string) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getUsersEndpoint()}/reset-password-with-code`,
            body: {
                code,
                password
            }
        });
    }

    public getEnhancedSigninInfo() {
        return this.apiModel.makeHttpObservableRequest<EnhancedSigninInfo>({
            method: 'GET',
            url: `${this.apiModel.getUsersEndpoint()}/introspection`
        });
    }

    public getAppUser(userId: string): Observable<AppUser> {
        return this.apiModel
            .makeHttpObservableRequest({
                method: 'GET',
                url: `${this.apiModel.getAppUsersEndpoint()}/${userId}`
            })
            .pipe(
                catchError((error) => {
                    console.error('Error getting app user', error);
                    return of(null);
                })
            );
    }

    public getAppUserOrganizations(userId: string): Observable<Auth0Organization[]> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getAppUsersEndpoint()}/${userId}/organizations`
        });
    }
}

export interface SignupRequest {
    _id?: string;
    organization: {
        name: string;
        phone: string;
        website?: string;
        gln?: string;
        type: OrganizationType;
        address: {
            line1: string;
            line2?: string;
            zip: string;
            city: string;
            country: string;
        };
        branch?: BranchToCreate;
    };
    user: {
        firstname: string;
        surname: string;
        email: string;
        preferredLanguage: string;
        department?: string;
        interests?: string[];
    };
    termsAccepted: boolean;
    marketingEmailConsentAccepted: boolean;
    signupReason?: string;
    heardAboutUs?: string;
    referrer?: string;
}

export interface SearchOrganizationResponse {
    _id: string;
    name: string;
    matchedBrand: string;
    address: {
        city: string;
        country: string;
        line1: string;
        zip: string;
    };
}

export interface ReferralSignup {
    _id?: string;
    organization: {
        name: string;
        phone: string;
        type: OrganizationType.Retailer;
        address: {
            line1: string;
            line2?: string;
            zip: string;
            city: string;
            country: string;
        };
    };
    user: {
        firstname: string;
        surname: string;
        email: string;
        preferredLanguage: string;
    };
    termsAccepted: true;
    referral: string;
}

export interface AppUser {
    userId: string;
    email: string;
    appMetadata?: Record<string, any>;
    userMetadata?: Record<string, any>;
    picture: string;
    name: string;
    emailVerified?: boolean;
    username?: string;
    blocked?: boolean;
    givenName?: string;
    familyName?: string;
}

export interface EnhancedSigninInfo {
    user: {
        _id: string;
        surname: string;
        firstname: string;
        emailVerified: boolean;
        organization: Organization;
        preferredLanguage: string;
        created: string;
        blocked: boolean;
        privileges: string[];
        email: string;
        accessToken: string;
    };
}
